import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSite } from '../context/SiteContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import styles from './Blog.module.css';

const CATEGORIES = [
  { id: 'all',         label: 'All Posts' },
  { id: 'trip-report', label: 'Trip Reports' },
  { id: 'tips',        label: 'Tips & Guides' },
  { id: 'news',        label: 'News' },
];

const CATEGORY_COLORS = {
  'trip-report': { color: '#2d7a3e', bg: '#e6f4ea' },
  'tips':        { color: '#1a56c4', bg: '#e8f0fe' },
  'news':        { color: '#b45309', bg: '#fef3c7' },
};

export default function Blog() {
  const { data } = useSite();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');

  const posts = (data.posts || []).filter(p => p.published);
  const filtered = activeCategory === 'all'
    ? posts
    : posts.filter(p => p.category === activeCategory);

  return (
    <>
      <SEO
        title="Hiking Blog | Jordan Trail Reviews, Tips & Stories"
        description="Read trail reviews, hiking tips, and stories from Jordan's outdoor community. Expert guides, destination reviews, and travel advice for hikers."
        image="https://keepersexperience.com/full_logo.png"
        url="https://keepersexperience.com/blog"
        type="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Keepers Blog",
          "description": "Trail reviews, tips, and news from Jordan's hiking community",
          "url": "https://keepersexperience.com/blog",
          "publisher": {
            "@type": "Organization",
            "name": "Keepers Experience",
            "logo": {
              "@type": "ImageObject",
              "url": "https://keepersexperience.com/full_logo.png"
            }
          }
        }}
      />
      <div className={styles.page}>
      <Navbar />

      <div className={styles.hero}>
        <motion.h1
          className={styles.heroTitle}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Keepers Blog
        </motion.h1>
        <motion.p
          className={styles.heroSub}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Trail reviews, tips, and news from Jordan's hiking community.
        </motion.p>
      </div>

      <div className={styles.container}>
        {/* Category tabs */}
        <div className={styles.tabs}>
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              className={`${styles.tab} ${activeCategory === cat.id ? styles.tabActive : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <p className={styles.empty}>No posts yet in this category.</p>
        ) : (
          <div className={styles.grid}>
            {filtered.map((post, i) => {
              const cat = CATEGORY_COLORS[post.category] || { color: '#555', bg: '#f0f0f0' };
              return (
                <motion.article
                  key={post.id}
                  className={styles.card}
                  style={{ '--accent': cat.color }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                  onClick={() => navigate(`/blog/${post.slug}`)}
                >
                  <div className={styles.cardImage}>
                    <img src={post.coverImage} alt={post.title} loading="lazy" />
                  </div>
                  <div className={styles.cardBody}>
                    <div className={styles.cardMeta}>
                      <span className={styles.catBadge} style={{ color: cat.color, background: cat.bg }}>
                        {CATEGORIES.find(c => c.id === post.category)?.label || post.category}
                      </span>
                      <span className={styles.cardDate}>{post.date}</span>
                    </div>
                    <h2 className={styles.cardTitle}>{post.title}</h2>
                    <p className={styles.cardExcerpt}>{post.excerpt}</p>
                    <span className={styles.readMore}>Read more →</span>
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
    </>
  );
}
